import React, { useContext } from 'react';
import { Box, makeStyles } from '@material-ui/core';
import { HomeComponentContainer } from './styled';
import { colorSet } from '@components/Provider';
import { CustomizedButton, CustomizedTypography } from '@components/Customized';
import { MediaQuery } from '@routes/Home/HomePresenter';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import CardMedia from '@material-ui/core/CardMedia';
import useUserActionLog, {
  Enum_Action_Button_Enum
} from '@hooks/useUserActionLog';
import { ArrowForward } from '@material-ui/icons';
import { CapaActivityType, CapaServiceType } from '@generated/graphql';
import useSwitchService from '@hooks/useSwitchService';
import { wordPressBlogFetcher } from '@api/rest';
import useSWR from 'swr';
import { useHistory } from 'react-router-dom';

interface Partner {
  id: string;
  slug: string;
  title: string;
  thumbnail: string;
}

const PartnerIntro: React.FC = () => {
  const history = useHistory();
  const { data } = useSWR(`/wp/v3/partners?limit=8`, wordPressBlogFetcher);

  const {
    container,
    cardContainer,
    card,
    cardContent,
    titleContainer,
    toInfo
  } = useStyles();
  const { md, sm } = useContext(MediaQuery);
  const { insertButtonLog, onInsertUserActionLogOne } = useUserActionLog();
  const { handleSwitchService } = useSwitchService();

  const onSendPartners = async () => {
    insertButtonLog('Button_Landing_JoinPartner');
    handleSwitchService(CapaServiceType.QuotationPartner);
  };

  const partners = (data?.data ?? []) as Partner[];

  return (
    <HomeComponentContainer
      className={container}
      padding={sm ? '100px 16px' : md ? '120px 0 100px' : '140px 0 120px'}
    >
      <Box className={titleContainer}>
        <CustomizedTypography variant="h1" fontWeight="700" align="left">
          믿을 수 있는 제조, <br />
          캐파 제조파트너를 소개합니다
        </CustomizedTypography>
        {!sm && (
          <CustomizedButton
            logId={
              Enum_Action_Button_Enum.Landing_MANUFACTURING_KNOWLEDGE_PARTNER_VIEW
            }
            className={toInfo}
            variant="text"
            color="primary"
            size="large"
            endIcon={<ArrowForward />}
            onClick={() => {
              history.push(`/blog/category/${encodeURI('캐파 파트너 소개')}`);
            }}
          >
            제조 파트너 소개 더보기
          </CustomizedButton>
        )}
      </Box>
      <Box className={cardContainer}>
        {partners.map((partner) => {
          const title = partner?.title
            ? partner.title
                ?.replace('[캐파 파트너스]', '')
                .replace('[CAPA 파트너스]', '')
                .trim()
            : '';

          const serviceType = title
            ?.slice(title?.lastIndexOf('('), title?.lastIndexOf(')') + 1)
            .replace('(', '')
            .replace(')', '');

          return (
            <Card
              className={card}
              key={partner?.id}
              onClick={() => {
                onInsertUserActionLogOne(
                  Enum_Action_Button_Enum.Landing_MANUFACTURING_KNOWLEDGE_PARTNER_CARD,
                  CapaActivityType.ButtonClick
                );

                history.push(`/blog/post/${partner?.slug}`);
              }}
            >
              <CardMedia
                width={210}
                height={sm ? 360 : 174}
                component="img"
                image={partner?.thumbnail}
              ></CardMedia>
              <CardContent className={cardContent}>
                <CustomizedTypography className="itemTitle" gutterBottoms={4}>
                  {title.lastIndexOf('(') !== -1
                    ? title.slice(0, title.lastIndexOf('('))
                    : title}
                </CustomizedTypography>
                <CustomizedTypography
                  variant={md || sm ? 'body2' : 'body1'}
                  fontWeight="700"
                >
                  {serviceType
                    ? serviceType
                    : title.includes('백두')
                    ? '다이캐스팅'
                    : ''}
                </CustomizedTypography>
              </CardContent>
            </Card>
          );
        })}
      </Box>
      {sm && (
        <CustomizedButton
          className={toInfo}
          variant="text"
          color="primary"
          logId={
            Enum_Action_Button_Enum.Landing_MANUFACTURING_KNOWLEDGE_PARTNER_VIEW
          }
          size="large"
          endIcon={<ArrowForward />}
          onClick={() => {
            history.push(`/blog/category/${encodeURI('캐파 파트너스')}`);
          }}
        >
          제조파트너 소개 더보기
        </CustomizedButton>
      )}
      <CustomizedButton
        className="registerBtn"
        size="large"
        onClick={onSendPartners}
      >
        제조 파트너로 가입하기
      </CustomizedButton>
    </HomeComponentContainer>
  );
};

export default PartnerIntro;

const useStyles = makeStyles((theme) => ({
  container: {
    backgroundColor: colorSet.gray100,
    textAlign: 'center',
    '& .registerBtn': {
      width: 222,
      marginTop: 54,
      backgroundColor: colorSet.gray900,
      fontSize: '16px',
      fontWeight: 500,
      '&:hover': {
        backgroundColor: colorSet.gray1000
      },
      [theme.breakpoints.down('md')]: {
        marginTop: 56
      },
      [theme.breakpoints.down('sm')]: {
        marginTop: 40,
        width: 343
      }
    }
  },
  titleContainer: {
    display: 'flex',
    flexFlow: 'row',
    justifyContent: 'space-between',
    marginBottom: '56px',
    [theme.breakpoints.down('sm')]: {
      flexFlow: 'column',
      marginBottom: '40px',
      '& h1': {
        fontSize: '28px'
      }
    }
  },
  cardContainer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
    flexFlow: 'wrap',
    gap: '24px',
    [theme.breakpoints.down('md')]: {
      gap: '16px'
    },
    [theme.breakpoints.down('sm')]: {
      display: 'grid',
      gridTemplateColumns: '1fr 1fr',
      gap: '15px'
    }
  },
  card: {
    minWidth: 222,
    maxWidth: 222,
    backgroundColor: colorSet.gray100,
    cursor: 'pointer',
    boxShadow: 'none',
    overflow: 'visible',
    transition: 'all 0.2s ease-in-out',
    '& .itemTitle': {
      fontSize: '18px',
      transition: 'all 0.2s ease-in-out'
    },
    '& img': {
      borderRadius: '16px',
      transition: 'all 0.2s ease-in-out'
    },
    '&:hover': {
      '& img': {
        transform: 'translateY(-10px)',
        boxShadow:
          'box-shadow: 0px 8px 16px 2px rgba(97, 97, 97, 0.1), 0px 16px 32px 2px rgba(97, 97, 97, 0.1)'
      },
      '& .itemTitle': {
        color: colorSet.primary500
      }
    },
    [theme.breakpoints.down('md')]: {
      minWidth: 210,
      maxWidth: `calc(100% / 4 - 16px)`
    },
    [theme.breakpoints.down('sm')]: {
      minWidth: 164,
      maxWidth: '100%',
      width: '100%',
      '& img': {
        maxHeight: 240
      }
    },
    [theme.breakpoints.down('xs')]: {
      '& img': {
        maxHeight: 140
      }
    }
  },
  cardContent: {
    textAlign: 'left',
    marginBottom: '16px',
    [theme.breakpoints.down('sm')]: {
      padding: '12px 8px',
      marginBottom: '23px'
    },
    '&:last-child': {
      paddingBottom: 0
    }
  },
  toInfo: {
    margin: 'auto 0 8px',
    '& > span': {
      fontSize: 20
    },
    '& svg': {
      marginLeft: '8px'
    },
    [theme.breakpoints.down('sm')]: {
      marginBottom: 0,
      marginTop: 21
    }
  }
}));
